<template>
  <component :is="iconComponent" v-bind="$attrs" />
</template>

<script setup lang="ts">
import {
  HomeIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarDaysIcon,
  UserIcon,
  ClockIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  PlusIcon,
  MinusIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  StarIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  ArrowLeftIcon,
  ArrowDownIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  PaperAirplaneIcon,
  UserGroupIcon,
  CameraIcon,
  CheckCircleIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  TruckIcon,
  AcademicCapIcon,
  BuildingOffice2Icon,
  XMarkIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  PencilSquareIcon,
  EyeIcon,
  EyeSlashIcon,
  TrashIcon,
  FunnelIcon,
  Squares2X2Icon,
  DocumentArrowDownIcon,
  CubeIcon,
  CurrencyEuroIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  ArrowUturnDownIcon,
  ArrowUturnLeftIcon,
  UsersIcon,
  UserCircleIcon,
  DocumentChartBarIcon,
  SparklesIcon,
  ChartBarIcon,
  HashtagIcon,
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  BeakerIcon,
  MapIcon,
  BookOpenIcon,
  ShoppingBagIcon,
  CloudArrowUpIcon,
  BackspaceIcon,
  GiftIcon,
  Bars3Icon,
  CalculatorIcon,
  InboxIcon,
  ArrowsPointingOutIcon,
  FolderOpenIcon,
  CreditCardIcon,
  DocumentIcon,
  ChatBubbleLeftRightIcon,
  BoltIcon,
  PrinterIcon,
  FlagIcon,
  PercentBadgeIcon,
  HomeModernIcon,
  ArrowRightCircleIcon,
  ArrowsRightLeftIcon,
  BellIcon,
  PhotoIcon,
  DocumentPlusIcon,
  ArrowUpTrayIcon,
  BriefcaseIcon,
  ArrowRightIcon,
  Cog6ToothIcon,
  BellSlashIcon,
  GlobeAltIcon,
  TicketIcon,
  LockClosedIcon,
  ArchiveBoxIcon,
  CircleStackIcon,
  TableCellsIcon,
  BanknotesIcon,
  IdentificationIcon,
  BuildingLibraryIcon,
  HeartIcon,
  MoonIcon,
  CakeIcon,
  SunIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
  LinkIcon,
  DevicePhoneMobileIcon,
  CubeTransparentIcon,
  AdjustmentsVerticalIcon,
  TagIcon,
} from "@heroicons/vue/24/outline";

import IconPlusCircleFilled from "~/components/icons/IconPlusCircleFilled.vue";
import IconAssurance from "~/components/icons/IconAssurance.vue";
import IconBusVip from "~/components/icons/IconBusVip.vue";
import IconChevronDown from "~/components/icons/IconChevronDown.vue";
import IconFoulard from "~/components/icons/IconFoulard.vue";
import IconKaaba from "~/components/icons/IconKaaba.vue";
import IconLivretRituels from "~/components/icons/IconLivretRituels.vue";
import IconMadinah from "~/components/icons/IconMadinah.vue";
import IconQods from "~/components/icons/IconQods.vue";
import IconSacADos from "~/components/icons/IconSacADos.vue";
import IconSacBanane from "~/components/icons/IconSacBanane.vue";
import IconTapisPriere from "~/components/icons/IconTapisPriere.vue";
import IconVisaTouristique from "~/components/icons/IconVisaTouristique.vue";
import IconVisitesGuidees from "~/components/icons/IconVisitesGuidees.vue";
import IconZamzam from "~/components/icons/IconZamzam.vue";

import {
  StarIcon as StarIconSolid,
  CheckCircleIcon as CheckCircleIconSolid,
  XCircleIcon as XCircleIconSolid,
  MapPinIcon as MapPinIconSolid,
  LockClosedIcon as LockClosedIconSolid,
} from "@heroicons/vue/24/solid";

interface Props {
  name: string;
}

const props = defineProps<Props>();

// Icon mapping for the icons used in the app
const iconMap: Record<string, any> = {
  "heroicons:home": HomeIcon,
  "heroicons:chevron-right": ChevronRightIcon,
  "heroicons:chevron-left": ChevronLeftIcon,
  "heroicons:chevron-down": ChevronDownIcon,
  "heroicons:chevron-up": ChevronUpIcon,
  "heroicons:arrow-up": ArrowUpIcon,
  "heroicons:arrow-left": ArrowLeftIcon,
  "heroicons:arrow-down": ArrowDownIcon,
  "heroicons:calendar-days": CalendarDaysIcon,
  "heroicons:user": UserIcon,
  "heroicons:user-circle": UserCircleIcon,
  "heroicons:clock": ClockIcon,
  "heroicons:phone": PhoneIcon,
  "heroicons:device-phone-mobile": DevicePhoneMobileIcon,
  "heroicons:email": EnvelopeIcon,
  "heroicons:envelope": EnvelopeIcon,
  "heroicons:map-pin": MapPinIcon,
  "heroicons:map-pin-solid": MapPinIconSolid,
  "heroicons:flag": FlagIcon,
  "heroicons:plus": PlusIcon,
  "heroicons:plus-circle": PlusCircleIcon,
  "heroicons:minus": MinusIcon,
  "heroicons:question-mark": QuestionMarkCircleIcon,
  "heroicons:information-circle": InformationCircleIcon,
  "heroicons:star-solid": StarIcon,
  "heroicons:check-circle": CheckCircleIcon,
  "heroicons:check-circle-solid": CheckCircleIconSolid,
  "heroicons:x-circle-solid": XCircleIconSolid,
  "heroicons:shield-check": ShieldCheckIcon,
  "heroicons:truck": TruckIcon,
  "heroicons:academic-cap": AcademicCapIcon,
  "heroicons:building-2": BuildingOffice2Icon,
  "heroicons:building-office-2": BuildingOffice2Icon,
  "heroicons:cake": CakeIcon,
  "heroicons:paper-airplane": PaperAirplaneIcon,
  "heroicons:x-circle": XCircleIcon,
  "heroicons:arrow-top-right-on-square": ArrowTopRightOnSquareIcon,
  "heroicons:x-mark": XMarkIcon,
  "heroicons:arrow-path": ArrowPathIcon,
  "heroicons:exclamation-circle": ExclamationCircleIcon,
  "heroicons:exclamation-triangle": ExclamationTriangleIcon,
  "heroicons:pencil": PencilIcon,
  "heroicons:pencil-square": PencilSquareIcon,
  "heroicons:eye": EyeIcon,
  "heroicons:eye-slash": EyeSlashIcon,
  "heroicons:trash": TrashIcon,
  "heroicons:funnel": FunnelIcon,
  "heroicons:star": StarIcon,
  "heroicons:cube": CubeIcon,
  "heroicons:cube-transparent": CubeTransparentIcon,
  "heroicons:currency-euro": CurrencyEuroIcon,
  "heroicons:building-office": BuildingOfficeIcon,
  "heroicons:magnifying-glass": MagnifyingGlassIcon,
  "heroicons:ellipsis-vertical": EllipsisVerticalIcon,
  "heroicons:document-duplicate": DocumentDuplicateIcon,
  "heroicons:cloud-arrow-up": CloudArrowUpIcon,
  "heroicons:arrow-down-tray": ArrowDownTrayIcon,
  "heroicons:arrow-uturn-down": ArrowUturnDownIcon,
  "heroicons:users": UsersIcon,
  "heroicons:document-chart-bar": DocumentChartBarIcon,
  "heroicons:sparkles": SparklesIcon,
  "heroicons:chart-bar": ChartBarIcon,
  "heroicons:hashtag": HashtagIcon,
  "heroicons:arrow-right-on-rectangle": ArrowRightOnRectangleIcon,
  "heroicons:calendar": CalendarIcon,
  "heroicons:calculator": CalculatorIcon,
  "heroicons:bars-3": Bars3Icon,
  "heroicons:inbox": InboxIcon,
  "heroicons:identification": IdentificationIcon,
  "heroicons:credit-card": CreditCardIcon,
  "heroicons:document": DocumentIcon,
  "heroicons:chat-bubble-left-right": ChatBubbleLeftRightIcon,
  "heroicons:lightning-bolt": BoltIcon,
  "heroicons:bolt": BoltIcon,
  "heroicons:printer": PrinterIcon,
  // Missing icons from seed-inclusions
  "heroicons:document-text": DocumentTextIcon,
  "heroicons:key": IdentificationIcon,
  "heroicons:document-check": DocumentCheckIcon,
  "heroicons:beaker": BeakerIcon,
  "heroicons:map": MapIcon,
  "heroicons:book-open": BookOpenIcon,
  "heroicons:shopping-bag": ShoppingBagIcon,
  "heroicons:backpack": BackspaceIcon,
  "heroicons:gift": GiftIcon,
  "heroicons:squares-2x2": Squares2X2Icon,
  "heroicons:percent-badge": PercentBadgeIcon,
  "heroicons:arrow-right-left": ArrowsRightLeftIcon,
  "heroicons:arrows-right-left": ArrowsRightLeftIcon,
  "heroicons:bell": BellIcon,
  "heroicons:keyboard": IdentificationIcon,
  "heroicons:photo": PhotoIcon,
  "heroicons:document-plus": DocumentPlusIcon,
  "heroicons:arrow-up-tray": ArrowUpTrayIcon,
  "heroicons:briefcase": BriefcaseIcon,
  "heroicons:user-group": UserGroupIcon,
  "heroicons:arrow-right": ArrowRightIcon,
  "heroicons:cog-6-tooth": Cog6ToothIcon,
  "heroicons:envelope-open": EnvelopeIcon,
  "heroicons:bell-slash": BellSlashIcon,
  "heroicons:clipboard-document-list": DocumentIcon,
  "heroicons:document-arrow-down": DocumentArrowDownIcon,
  "heroicons:check": CheckCircleIcon,
  "heroicons:globe-alt": GlobeAltIcon,
  "heroicons:ticket": TicketIcon,
  "heroicons:document-magnifying-glass": MagnifyingGlassIcon,
  "heroicons:user-plus": UserIcon,
  "heroicons:lock-closed": LockClosedIcon,
  "heroicons:lock-closed-solid": LockClosedIconSolid,
  "heroicons:archive-box": ArchiveBoxIcon,
  "heroicons:circle-stack": CircleStackIcon,
  "heroicons:table-cells": TableCellsIcon,
  "heroicons:light-bulb": LightBulbIcon,
  "heroicons:banknotes": BanknotesIcon,
  "heroicons:arrow-uturn-left": ArrowUturnLeftIcon,
  "heroicons:building-library": BuildingLibraryIcon,
  "custom:plus-circle-filled": IconPlusCircleFilled,
  "plus-circle-filled": IconPlusCircleFilled,
  "custom:visa-touristique": IconVisaTouristique,
  "custom:assurance-medicale": IconAssurance,
  "custom:bus-vip": IconBusVip,
  "custom:chevron-down": IconChevronDown,
  "custom:visites-guidees": IconVisitesGuidees,
  "custom:madinah": IconMadinah,
  "custom:kaaba": IconKaaba,
  "custom:zamzam": IconZamzam,
  "custom:tapis-priere": IconTapisPriere,
  "custom:livret-rituels": IconLivretRituels,
  "custom:sac-banane": IconSacBanane,
  "custom:sac-a-dos": IconSacADos,
  "custom:foulard": IconFoulard,
  "custom:al-qods": IconQods,
  "heroicons:heart": HeartIcon,
  "heroicons:clipboard-document-check": DocumentCheckIcon,
  "heroicons:chat-bubble-bottom-center-text": ChatBubbleLeftRightIcon,
  "heroicons:link": LinkIcon,
  "heroicons:folder-open": FolderOpenIcon,
  "heroicons:moon": MoonIcon,
  "heroicons:sun": SunIcon,
  "heroicons:magnifying-glass-minus": MagnifyingGlassMinusIcon,
  "heroicons:magnifying-glass-plus": MagnifyingGlassPlusIcon,
  "heroicons:arrows-pointing-out": ArrowsPointingOutIcon,
  "heroicons:email-check": EnvelopeIcon,
  "heroicons:adjustments-vertical": AdjustmentsVerticalIcon,
  "heroicons:tag": TagIcon,

  // Short aliases for commonly used icons
  home: HomeIcon,
  plus: PlusIcon,
  "arrow-up": ArrowUpIcon,
  "chevron-up": ChevronUpIcon,
  refresh: ArrowPathIcon,
  loading: ArrowPathIcon, // Will need custom animation
  "alert-circle": ExclamationCircleIcon,
  building: BuildingOffice2Icon, // Use BuildingOffice2Icon for better consistency
  "star-filled": StarIconSolid,
  star: StarIcon,
  edit: PencilIcon,
  "eye-off": EyeSlashIcon,
  eye: EyeIcon,
  delete: TrashIcon,
  "alert-triangle": ExclamationTriangleIcon,
  "information-circle": InformationCircleIcon,
  x: XMarkIcon,
  hotel: BuildingOfficeIcon,
  filter: FunnelIcon,
  calendar: CalendarIcon,
  trash: TrashIcon,
  pencil: PencilIcon,
  lightbulb: LightBulbIcon,
  info: InformationCircleIcon,
  "x-circle": XCircleIcon,
  grid: Squares2X2Icon,
  download: DocumentArrowDownIcon,
  export: DocumentArrowDownIcon,
  user: UserIcon,
  phone: PhoneIcon,
  users: UsersIcon,
  "chevron-down": ChevronDownIcon,
  "external-link": ArrowTopRightOnSquareIcon,
  envelope: EnvelopeIcon,
  document: DocumentDuplicateIcon,
  "check-circle": CheckCircleIcon,
  "document-chart-bar": DocumentChartBarIcon,
  "currency-euro": CurrencyEuroIcon,
  "chart-bar": ChartBarIcon,

  "mdi:hotel": BuildingOffice2Icon,
  "mdi:calendar": CalendarIcon,
  "mdi:airplane": PaperAirplaneIcon,
  "mdi:facebook": UserGroupIcon,
  "mdi:instagram": CameraIcon,
};

const iconComponent = computed(() => {
  const icon = iconMap[props.name];
  if (!icon) {
    return QuestionMarkCircleIcon;
  }
  return icon;
});
</script>
